import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AUTH_ACTIVITY, can, canAccessPage, defaultPageForRole, type Page } from '@agrogruda/shared';
import { useAuth } from '@/auth/useAuth';
import { UsersModal } from '@/components/users/UsersModal';
import { useToast } from '@/components/ui/Toast';
import { PAGE_PATHS, pageFromPath } from '@/config/pages';
import { TasksSessionEffects } from '@/features/tasks/TasksSessionEffects';
import { WaNavBadgeSync } from '@/features/whatsapp';
import { sq } from '@/i18n/sq';
import { logActivity } from '@/lib/activity';
import { disablePush } from '@/push/subscription';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

/** Prototype .app grid (line 898): sidebar + main (topbar + content). Modals are portaled to <body>. */
export function AppShell() {
  const { profile, role, signOut } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);

  const page = pageFromPath(location.pathname);
  const allowed = canAccessPage(role, page);

  // Prototype go(): pages outside ROLE_PAGES show 'Nuk ke qasje në këtë pjesë' and go back to the role's
  // own start page. Arriving at the dashboard is simply where every login lands first, so a role without
  // one (a delivery worker) is taken to his start page without being told off for it.
  //
  // The redirect itself is rendered (<Navigate> below), keyed by the navigation, not fired once. Right after
  // a login the login page's own navigation can land straight after the redirect and put the till back;
  // nothing the guard looked at had changed, so it never fired again, and a courier on a slow phone was
  // left on an empty till page. Every navigation has its own key, so every one is checked.
  const blocked = role !== null && !allowed;
  useEffect(() => {
    if (blocked && page !== 'dashboard') toast(sq.nav.noAccess);
  }, [blocked, page, toast]);

  const go = useCallback(
    (target: Page) => {
      if (!canAccessPage(role, target)) {
        toast(sq.nav.noAccess);
        return;
      }
      void navigate(PAGE_PATHS[target]);
      setSidebarOpen(false);
    },
    [role, toast, navigate],
  );

  const openUsers = useCallback(() => {
    if (!can(role, 'users.manage')) {
      toast(sq.auth.onlyAdminManagesUsers);
      return;
    }
    setUsersOpen(true);
  }, [role, toast]);

  const closeUsers = useCallback(() => setUsersOpen(false), []);

  const logout = useCallback(async () => {
    if (profile) await logActivity(profile, 'auth', AUTH_ACTIVITY.logout, profile.name);
    // DELETE /api/push/subscribe needs the session token → before signOut (task.md §4.8).
    await disablePush();
    await signOut();
    void navigate('/login', { replace: true });
  }, [profile, signOut, navigate]);

  return (
    <>
      <TasksSessionEffects />
      <WaNavBadgeSync />
      <div className="app">
        <Sidebar
          open={sidebarOpen}
          activePage={page}
          userName={profile?.name ?? ''}
          role={role}
          canManageUsers={can(role, 'users.manage')}
          onNavigate={go}
          onOpenUsers={openUsers}
          onLogout={() => void logout()}
        />
        <main className="main">
          <Topbar page={page} sidebarOpen={sidebarOpen} onToggleSidebar={() => setSidebarOpen((v) => !v)} />
          <div className="content">
            {allowed ? (
              <Outlet />
            ) : blocked ? (
              <Navigate key={location.key} to={PAGE_PATHS[defaultPageForRole(role)]} replace />
            ) : null}
          </div>
        </main>
      </div>
      <UsersModal open={usersOpen} onClose={closeUsers} />
    </>
  );
}
